import {
  Box,
  Container,
  Text,
  Title,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Badge,
} from '@mantine/core';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import { trackCTAClick, trackLinkClick } from '../analytics';
import {
  IconRocket,
  IconBrain,
  IconShieldLock,
  IconWorld,
  IconUsers,
  IconGlobe,
  IconCalendarEvent,
  IconBrandGoogle,
  IconBrandGithub,
  IconBrandNotion,
  IconBrandTrello,
  IconShare,
  IconUsersGroup,
  IconSearch,
  IconMicrophone,
} from '@tabler/icons-react';

/* ================================================================
   CSS — animations, responsive grid, marquee, glow effects
   ================================================================ */
const CSS = `
  /* ── Glow CTA ── */
  .glow-btn {
    animation: glow-pulse 3s ease-in-out infinite;
    transition: all .3s ease !important;
  }
  .glow-btn:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 0 40px rgba(253,126,20,.5), 0 0 100px rgba(253,126,20,.12) !important;
  }
  .glow-btn:active { transform: translateY(0) scale(.98); }
  @keyframes glow-pulse {
    0%,100% { box-shadow: 0 0 20px rgba(253,126,20,.3), 0 0 60px rgba(253,126,20,.06); }
    50%     { box-shadow: 0 0 30px rgba(253,126,20,.45), 0 0 80px rgba(253,126,20,.1); }
  }

  /* ── Feature card hover ── */
  .feat-card { transition: all .3s cubic-bezier(.25,.4,.25,1); height: 100%; }
  .feat-card:hover {
    transform: translateY(-4px);
    border-color: rgba(253,126,20,.15) !important;
    box-shadow: 0 8px 40px rgba(253,126,20,.06);
  }
  .feat-card-soon { transition: all .3s cubic-bezier(.25,.4,.25,1); height: 100%; }
  .feat-card-soon:hover {
    transform: translateY(-2px);
    border-color: rgba(255,255,255,.1) !important;
  }

  /* ── Feature grid ── */
  .feat-grid { display:grid; grid-template-columns:1fr; gap:16px; }
  @media(min-width:640px)  { .feat-grid { grid-template-columns:repeat(2,1fr); } }
  @media(min-width:960px)  { .feat-grid { grid-template-columns:repeat(3,1fr); } }

  /* ── Roadmap grid ── */
  .soon-grid { display:grid; grid-template-columns:1fr; gap:12px; max-width:900px; margin:0 auto; }
  @media(min-width:640px)  { .soon-grid { grid-template-columns:repeat(2,1fr); } }
  @media(min-width:960px)  { .soon-grid { grid-template-columns:repeat(3,1fr); } }

  /* ── Floating orb ── */
  @keyframes float-slow {
    0%,100% { transform: translate(0,0); }
    33%     { transform: translate(30px,-20px); }
    66%     { transform: translate(-20px,15px); }
  }
`;

/* ================================================================
   Animation helpers
   ================================================================ */
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: i * 0.12,
      ease: [0.25, 0.4, 0.25, 1] as [number, number, number, number],
    },
  }),
};

const VP = { once: true, margin: '-80px' as any };

/* ================================================================
   Data
   ================================================================ */
export const FEATURES = [
  {
    icon: IconRocket,
    title: 'Deploy in Minutes',
    desc: 'Go from sign-up to a live AI agent on Telegram in under five minutes. No code, no DevOps.',
  },
  {
    icon: IconBrandGoogle,
    title: 'Google Workspace',
    desc: 'Schedule events, reply to emails, and access Drive files — all from your chat. Your agent works where you do.',
  },
  {
    icon: IconCalendarEvent,
    title: 'Smart Scheduling',
    desc: 'Recurring tasks, reminders, and automated routines. Your agent works on a schedule — even while you sleep.',
  },
  {
    icon: IconGlobe,
    title: 'Web Browsing',
    desc: 'Search the internet, navigate websites, and bring back what you need. Your agent browses for you.',
  },
  {
    icon: IconBrain,
    title: 'Deep Memory',
    desc: 'Semantic vector search and persistent context. Your agent remembers everything that matters.',
  },
  {
    icon: IconShieldLock,
    title: 'Total Isolation',
    desc: 'Every agent runs in its own secure pod. Zero shared data. Zero compromise.',
  },
  {
    icon: IconMicrophone,
    title: 'Voice Messages',
    desc: 'Send voice notes on Telegram and your agent understands them. Powered by speech-to-text transcription.',
  },
  {
    icon: IconBrandNotion,
    title: 'Notion',
    desc: 'Create and search pages, databases, and docs.',
  },
  {
    icon: IconBrandGithub,
    title: 'GitHub',
    desc: 'Manage repos, issues, and PRs from your chat. Stay on top of your codebase without leaving the conversation.',
  },
];

export const COMING_SOON = [
  { icon: IconBrandTrello, title: 'Trello', desc: 'Manage boards, cards, and checklists from your chat.' },
  { icon: IconUsers, title: 'Multi-Agent', desc: 'Specialized agents for different roles, one platform.' },
  { icon: IconWorld, title: 'More Channels', desc: 'WhatsApp, Discord, Slack & more — simultaneously.' },
];

const STEPS = [
  { n: '01', title: 'Sign Up', desc: 'Connect via Telegram in seconds. No forms, no friction.' },
  { n: '02', title: 'Configure', desc: 'Choose your agent\'s purpose, personality, and skills through a guided wizard.' },
  { n: '03', title: 'Deploy', desc: 'One tap and your agent goes live on Telegram. More channels coming soon.' },
];

const STATS = [
  { value: '24/7', label: 'Always On' },
  { value: '<5', label: 'Min to Deploy' },
  { value: '100%', label: 'Isolated' },
  { value: '∞', label: 'Memory' },
];

/* ================================================================
   Shared style tokens
   ================================================================ */
const gradientText: React.CSSProperties = {
  background: 'linear-gradient(135deg, #fd7e14, #ff922b)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

/* ================================================================
   Small reusable pieces
   ================================================================ */

function FeatureCard({
  icon: Icon,
  title,
  desc,
  soon,
}: {
  icon: typeof IconWorld;
  title: string;
  desc: string;
  soon?: boolean;
}) {
  return (
    <Box
      className={soon ? 'feat-card-soon' : 'feat-card'}
      p="xl"
      style={{
        background: soon ? 'rgba(255,255,255,.015)' : 'rgba(255,255,255,.025)',
        border: `1px solid ${soon ? 'rgba(255,255,255,.04)' : 'rgba(255,255,255,.06)'}`,
        borderRadius: 16,
      }}
    >
      <Group justify="space-between" align="flex-start">
        <Box
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: soon ? 'rgba(255,255,255,.03)' : 'rgba(253,126,20,.08)',
            border: `1px solid ${soon ? 'rgba(255,255,255,.06)' : 'rgba(253,126,20,.12)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon
            size={24}
            color={soon ? 'rgba(255,255,255,.35)' : '#fd7e14'}
            stroke={1.5}
          />
        </Box>
        {soon && (
          <Badge
            variant="light"
            color="gray"
            size="xs"
            radius="sm"
            style={{ textTransform: 'none', fontWeight: 500, fontSize: 10 }}
          >
            Coming Soon
          </Badge>
        )}
      </Group>
      <Text
        fw={600}
        size={soon ? 'md' : 'lg'}
        mt="md"
        style={{ color: soon ? 'rgba(255,255,255,.55)' : '#fff' }}
      >
        {title}
      </Text>
      <Text size="sm" c="dimmed" mt={8} lh={1.6} style={soon ? { opacity: 0.7 } : undefined}>
        {desc}
      </Text>
    </Box>
  );
}

/* ================================================================
   LANDING PAGE
   ================================================================ */
export default function LandingPage() {
  const navigate = useNavigate();
  const go = () => navigate('/login');

  return (
    <Box component="main" style={{ position: 'relative', zIndex: 1 }}>
      <SEO
        title="Haive — Deploy Your Personal AI Agent in Minutes"
        description="Deploy your personal AI agent on Telegram in under 5 minutes. No code, no DevOps. Google Calendar, Gmail, Notion, web browsing, voice messages and deep memory — powered by OpenClaw."
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          'name': 'Haive',
          'url': 'https://haive.dev',
          'description': 'Deploy your personal AI agent in minutes — no code required. Powered by OpenClaw, the open-source multi-channel AI gateway.',
          'applicationCategory': 'BusinessApplication',
          'operatingSystem': 'Web',
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'EUR',
            'description': 'Free tier with 500 credits',
          },
          'creator': {
            '@type': 'Organization',
            'name': 'Haive',
            'url': 'https://haive.dev',
            'sameAs': [
              'https://haive.chat',
              'https://haive.live',
              'https://haive.technology',
            ],
          },
        }}
      />
      <style>{CSS}</style>


      {/* ══════════════════════════════════════════════════════════
         HERO
         ══════════════════════════════════════════════════════════ */}
      <Box
        component="section"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          paddingTop: 100,
          paddingBottom: 40,
        }}
      >
        <Box
          style={{
            position: 'absolute',
            width: '90%',
            maxWidth: 800,
            height: 500,
            top: '42%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            background:
              'radial-gradient(ellipse, rgba(253,126,20,.1) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <Container size="md" style={{ position: 'relative', zIndex: 1 }}>
          <Stack gap="xl" align="center" ta="center">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0}
            >
              <Badge
                variant="outline"
                color="haiveOrange"
                size="lg"
                radius="xl"
                style={{
                  borderColor: 'rgba(253,126,20,.3)',
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Alpha — Now Open for Early Users
              </Badge>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={1}
            >
              <Title
                order={1}
                style={{
                  fontSize: 'clamp(2.5rem, 7vw, 4.5rem)',
                  fontWeight: 800,
                  lineHeight: 1.1,
                  letterSpacing: '-.03em',
                }}
              >
                Deploy AI Agents
                <br />
                <span style={gradientText}>That Never Sleep.</span>
              </Title>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={2}
            >
              <Text
                c="dimmed"
                maw={560}
                mx="auto"
                style={{
                  fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                  lineHeight: 1.7,
                }}
              >
                Your personal AI agent — deployed on Telegram in minutes.
                It manages your calendar, replies to emails, browses the web,
                schedules tasks, and never stops working.
              </Text>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={3}
            >
              <Group gap="md" justify="center" wrap="wrap">
                <Button
                  size="xl"
                  color="haiveOrange"
                  radius="xl"
                  className="glow-btn"
                  rightSection={<IconRocket size={20} />}
                  onClick={() => { trackCTAClick('Get Started Free', 'hero', '/login'); go(); }}
                  style={{
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    paddingInline: 32,
                  }}
                >
                  Get Started Free
                </Button>
                <Button
                  size="xl"
                  variant="subtle"
                  color="gray"
                  radius="xl"
                  component="a"
                  href="#how-it-works"
                  onClick={() => trackLinkClick('See How It Works', '#how-it-works', false)}
                  style={{ fontWeight: 500 }}
                >
                  See How It Works
                </Button>
              </Group>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.3 }}
              transition={{ delay: 2, duration: 1 }}
            >
              <Box
                mt="xl"
                style={{
                  width: 24,
                  height: 40,
                  borderRadius: 12,
                  border: '2px solid rgba(255,255,255,.2)',
                  display: 'flex',
                  justifyContent: 'center',
                  paddingTop: 8,
                }}
              >
                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,.5)',
                  }}
                />
              </Box>
            </motion.div>
          </Stack>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════════
         AGENT MARKETPLACE TEASER
         ══════════════════════════════════════════════════════════ */}
      <Box
        component="section"
        py={{ base: 56, md: 80 }}
        style={{ position: 'relative', zIndex: 1 }}
      >
        <Container size="md">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
            custom={0}
          >
            <Box
              style={{
                background: 'rgba(255,255,255,.02)',
                border: '1px solid rgba(255,255,255,.05)',
                borderRadius: 20,
                padding: 'clamp(32px, 5vw, 48px)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Subtle glow inside the card */}
              <Box
                style={{
                  position: 'absolute',
                  width: 400,
                  height: 400,
                  borderRadius: '50%',
                  background: 'rgba(253,126,20,.06)',
                  filter: 'blur(80px)',
                  top: -200,
                  right: -100,
                  pointerEvents: 'none',
                }}
              />

              <Stack gap="lg" style={{ position: 'relative' }}>
                <Badge
                  variant="light"
                  color="gray"
                  size="sm"
                  radius="sm"
                  style={{ textTransform: 'none', fontWeight: 500, alignSelf: 'flex-start', fontSize: 11 }}
                >
                  Coming Soon
                </Badge>

                <Title
                  order={2}
                  style={{
                    fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                    fontWeight: 800,
                    letterSpacing: '-.02em',
                  }}
                >
                  The Agent{' '}
                  <span style={gradientText}>Marketplace.</span>
                </Title>

                <Text c="dimmed" size="md" maw={480} lh={1.7}>
                  Share agents with friends, trade configurations with your team,
                  or discover agents built by the community. One tap to install.
                </Text>

                <SimpleGrid
                  cols={{ base: 1, xs: 3 }}
                  spacing="lg"
                  mt="xs"
                >
                  <Group gap="sm" wrap="nowrap">
                    <IconShare size={20} color="rgba(253,126,20,.7)" stroke={1.5} />
                    <Text size="sm" c="dimmed" fw={500}>Share with friends</Text>
                  </Group>
                  <Group gap="sm" wrap="nowrap">
                    <IconUsersGroup size={20} color="rgba(253,126,20,.7)" stroke={1.5} />
                    <Text size="sm" c="dimmed" fw={500}>Trade across teams</Text>
                  </Group>
                  <Group gap="sm" wrap="nowrap">
                    <IconSearch size={20} color="rgba(253,126,20,.7)" stroke={1.5} />
                    <Text size="sm" c="dimmed" fw={500}>Discover & install</Text>
                  </Group>
                </SimpleGrid>
              </Stack>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════════
         FEATURES — AVAILABLE NOW
         ══════════════════════════════════════════════════════════ */}
      <Box
        component="section"
        id="features"
        py={{ base: 80, md: 120 }}
        style={{ position: 'relative', zIndex: 1 }}
      >
        <Container size="lg">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
            custom={0}
          >
            <Text
              ta="center"
              size="sm"
              tt="uppercase"
              fw={600}
              c="haiveOrange"
              mb="xs"
              style={{ letterSpacing: '.12em' }}
            >
              Available Now
            </Text>
            <Title
              order={2}
              ta="center"
              mb={48}
              style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                fontWeight: 800,
              }}
            >
              Everything You Need.
              <br />
              Nothing You Don't.
            </Title>
          </motion.div>

          <div className="feat-grid">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={VP}
                custom={i}
              >
                <FeatureCard {...f} />
              </motion.div>
            ))}
          </div>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════════
         HOW IT WORKS
         ══════════════════════════════════════════════════════════ */}
      <Box
        component="section"
        id="how-it-works"
        py={{ base: 80, md: 120 }}
        style={{ position: 'relative', zIndex: 1 }}
      >
        <Container size="lg">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
            custom={0}
          >
            <Text
              ta="center"
              size="sm"
              tt="uppercase"
              fw={600}
              c="haiveOrange"
              mb="xs"
              style={{ letterSpacing: '.12em' }}
            >
              How It Works
            </Text>
            <Title
              order={2}
              ta="center"
              mb={56}
              style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                fontWeight: 800,
              }}
            >
              From Zero to Deployed.
              <br />
              Three Steps.
            </Title>
          </motion.div>

          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xl">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={VP}
                custom={i}
              >
                <Stack gap="sm" align="center" ta="center">
                  <Text
                    fw={800}
                    style={{
                      fontSize: 'clamp(3rem, 6vw, 4rem)',
                      lineHeight: 1,
                      ...gradientText,
                    }}
                  >
                    {s.n}
                  </Text>
                  <Text fw={700} size="xl" style={{ color: '#fff' }}>
                    {s.title}
                  </Text>
                  <Text size="sm" c="dimmed" maw={280} lh={1.6}>
                    {s.desc}
                  </Text>
                </Stack>
              </motion.div>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════════
         COMING SOON / ROADMAP
         ══════════════════════════════════════════════════════════ */}
      <Box
        component="section"
        py={{ base: 64, md: 100 }}
        style={{ position: 'relative', zIndex: 1 }}
      >
        <Container size="lg">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
            custom={0}
          >
            <Text
              ta="center"
              size="sm"
              tt="uppercase"
              fw={600}
              c="dimmed"
              mb="xs"
              style={{ letterSpacing: '.12em' }}
            >
              On the Roadmap
            </Text>
            <Title
              order={2}
              ta="center"
              mb={40}
              style={{
                fontSize: 'clamp(1.5rem, 3.5vw, 2rem)',
                fontWeight: 700,
                color: 'rgba(255,255,255,.7)',
              }}
            >
              And We're Just Getting Started.
            </Title>
          </motion.div>

          <div className="soon-grid">
            {COMING_SOON.map((f, i) => (
              <motion.div
                key={f.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={VP}
                custom={i}
              >
                <FeatureCard {...f} soon />
              </motion.div>
            ))}
          </div>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════════
         STATS
         ══════════════════════════════════════════════════════════ */}
      <Box
        component="section"
        py={{ base: 48, md: 80 }}
        style={{ position: 'relative', zIndex: 1 }}
      >
        <Container size="lg">
          <Box
            style={{
              background: 'rgba(255,255,255,.025)',
              border: '1px solid rgba(255,255,255,.06)',
              borderRadius: 20,
              padding: 'clamp(32px, 5vw, 56px)',
            }}
          >
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xl">
              {STATS.map((s, i) => (
                <motion.div
                  key={s.label}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={VP}
                  custom={i}
                >
                  <Stack gap={4} align="center">
                    <Text
                      fw={800}
                      style={{
                        fontSize: 'clamp(2rem, 5vw, 3rem)',
                        lineHeight: 1,
                        ...gradientText,
                      }}
                    >
                      {s.value}
                    </Text>
                    <Text
                      size="sm"
                      c="dimmed"
                      fw={500}
                      tt="uppercase"
                      style={{ letterSpacing: '.1em' }}
                    >
                      {s.label}
                    </Text>
                  </Stack>
                </motion.div>
              ))}
            </SimpleGrid>
          </Box>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════════
         FINAL CTA
         ══════════════════════════════════════════════════════════ */}
      <Box
        component="section"
        py={{ base: 80, md: 140 }}
        style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}
      >
        <Box
          style={{
            position: 'absolute',
            width: '100%',
            maxWidth: 1000,
            height: 600,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            background:
              'radial-gradient(ellipse, rgba(253,126,20,.08) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <Container size="sm" style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
            custom={0}
          >
            <Title
              order={2}
              mb="md"
              style={{
                fontSize: 'clamp(2rem, 5vw, 3rem)',
                fontWeight: 800,
              }}
            >
              Ready to Deploy
              <br />
              <span style={gradientText}>Your AI Agent?</span>
            </Title>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
            custom={1}
          >
            <Text c="dimmed" mb={32} size="lg">
              Join the alpha. Be one of the first.
            </Text>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
            custom={2}
          >
            <Button
              size="xl"
              color="haiveOrange"
              radius="xl"
              className="glow-btn"
              rightSection={<IconRocket size={20} />}
              onClick={() => { trackCTAClick("Get Started — It's Free", 'final-cta', '/login'); go(); }}
              style={{
                fontWeight: 700,
                fontSize: '1.1rem',
                paddingInline: 40,
              }}
            >
              Get Started — It's Free
            </Button>
          </motion.div>
        </Container>
      </Box>

    </Box>
  );
}
